const SESSION_KEY = "login-response";

/** Match setCookie path=/ so JS-writable cookies are actually expired. */
const deleteCookie = (cname: string): void => {
  document.cookie = cname + "=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/";
};

export type LoginResponse = {
  status: string;
  name: string;
  orgId: string;
  isAgp: string;
  email: string;
  mobile: string;
  countryCode: string;
  id: string;
  usertype: string[];
  userId: string;
  roles: string[];
  accessibleScreens: unknown[];
  subscriptionExpiryDate: string;
  welcomeNotification: Record<string, string>;
  permissions: {
    appName: string;
    permissions: Permission[];
  };
  canDeactivateRestore: boolean;
  defaultOrgId: string;
  dashboardPages: string[];
  token: string;
  refreshToken: string;
  exp?: string | number;
};

type Permission = {
  moduleName: string;
  operations: Operation[];
};

type Operation = {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
};

export const getLoginResponse = (): LoginResponse | null => {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LoginResponse;
  } catch {
    return null;
  }
};

/**
 * Profile in localStorage is the FE "logged in" signal.
 * Auth tokens live in HttpOnly cookies (`token`, `refresh_token`) which JS cannot read —
 * so cookie existence cannot be used as the logged-in check.
 */
export const isLoggedIn = (): boolean => getLoginResponse() !== null;

export const saveSession = (session: LoginResponse): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

/** Clears FE session storage and best-effort cookie deletes (HttpOnly cookies need BE logout). */
export const clearSession = (): void => {
  localStorage.clear();
  sessionStorage.clear();
  // Non-HttpOnly companion cookie set on login
  deleteCookie("landlordUser");
  // Best-effort: HttpOnly auth cookies often cannot be cleared from JS (Path/Domain/HttpOnly);
  // backend logout / Set-Cookie expiry remains the reliable clear path.
  deleteCookie("token");
  deleteCookie("refresh_token");
};

export const getAccessToken = (): string | null => getLoginResponse()?.token ?? null;

export const getRefreshToken = (): string | null => getLoginResponse()?.refreshToken ?? null;
